import { ObjID } from 'src/app/interfaces/object-id.interface';
import { BandState, BandData, BandType } from 'src/app/interfaces/band-models.interface';
import { Node, NodeType } from 'src/app/interfaces/node-models.interface';
import { Workflow, Workspace } from 'src/app/interfaces/workspace.interface';
import { BehaviorSubject } from 'rxjs';
import { getUUID } from '../id-utils/id.utils';
import { Point } from '@angular/cdk/drag-drop';
import { GRID_SIZE } from 'src/app/services/workspace/workspace.const';
import { BandModel } from 'src/app/interfaces/workflow.interface';

/** Data structure to track the workspace nodes, bands, and workflows */
export class Graph {
	/** Internal data structures to support the graph */
	private linkMap: Map<ObjID, BandState[]> = new Map<ObjID, BandState[]>();
	private reverseLinks: Map<ObjID, ObjID[]> = new Map<ObjID, ObjID[]>();
	private nodeMap: Map<ObjID, Node> = new Map<ObjID, Node>();
	private workflowMap: Map<ObjID, Workflow> = new Map<ObjID, Workflow>();

	/** Constructor */
	constructor(workspace?: Workspace) {
		if (workspace) {
			this.initialize(workspace);
		}
	}

	/**
	 * Public Functions
	 */

	/** Check if a nodeId exists as a source in the linkMap */
	hasBandStates(srcId: ObjID): boolean {
		return this.linkMap.has(srcId);
	}

	/** Checks if a band exists between two nodes */
	hasLink(srcId: ObjID, destId: ObjID): boolean {
		return !!this.getLink(srcId, destId);
	}

	/** Returns a list of all output bands from a node */
	getBandStates(srcId: ObjID): BandState[] {
		return this.linkMap.get(srcId) || [];
	}

	/** Returns a band between two specific nodes */
	getLink(srcId: ObjID, destId: ObjID): BandState {
		return this.getBandStates(srcId)?.find((bandState: BandState) => bandState.destId === destId);
	}

	/** Adds a band to the LinkMap */
	addLink(srcId: ObjID, dest: BandState): void {
		if (this.hasLink(srcId, dest.destId)) {
			return;
		}
		// Add link to LinkMap
		if (this.linkMap.has(srcId)) {
			this.linkMap.get(srcId).push(dest);
		} else {
			this.linkMap.set(srcId, [dest]);
		}
		// Add link to reverse LinkMap
		if (this.reverseLinks.has(dest.destId)) {
			this.reverseLinks.get(dest.destId).push(srcId);
		} else {
			this.reverseLinks.set(dest.destId, [srcId]);
		}
		// Update workflows
		this.updateWorkflowsOnConnect(srcId, dest.destId);
	}

	/** Removes a band from the LinkMap */
	removeLink(srcId: ObjID, destId: ObjID): BandState {
		if (!this.linkMap.has(srcId)) {
			return;
		}
		const links: BandState[] = this.linkMap.get(srcId);
		const idx: number = links.findIndex((band: BandState) => band.destId === destId);
		let bandState: BandState = null;
		if (idx < 0) {
			return;
		}
		bandState = links[idx];
		links.splice(idx, 1);
		if (!links.length) {
			this.linkMap.delete(srcId);
		}
		// end position listener
		this.unsubscribeBandListener(bandState);
		this.removeReverseLink(srcId, destId);
		this.updateWorkflowsOnDisconnect(srcId, destId);
		return bandState;
	}

	/** Adds a node to the NodeMap */
	addNode(node: Node): void {
		this.nodeMap.set(node.id, node);
		const pos = { ...node.position };
		// Move node to open space
		while (this.nodeOverlapsAtPosition(pos, [node.id])) {
			pos.x += GRID_SIZE;
			pos.y += GRID_SIZE;
		}
		node.position = pos;
	}

	/** Check if the NodeMap contains a node with a given ID */
	hasNode(nodeId: ObjID): boolean {
		return this.nodeMap.has(nodeId);
	}

	/** Get a node by ID from the NodeMap */
	getNode(nodeId: ObjID): Node {
		return this.nodeMap.get(nodeId);
	}

	/** Get the IDs of all nodes in the NodeMap */
	getNodeIds(): ObjID[] {
		return Array.from(this.nodeMap.keys());
	}

	/** Get a list of all nodes in the NodeMap */
	getNodes(): Node[] {
		return Array.from(this.nodeMap.values());
	}

	/** Remove a node with a given ID from the NodeMap */
	removeNode(nodeId: ObjID, parentId?: ObjID) {
		if (parentId) {
			const node = this.nodeMap.get(parentId);
			node.children = node.children.filter(child => child.id !== nodeId);
		} else {
			this.removeAllLinksTo(nodeId);
			this.nodeMap.delete(nodeId);
		}
	}

	/** Moves a node to a new position */
	moveNode(nodeId: ObjID, position: Point) {
		const node = this.nodeMap.get(nodeId);
		if (node) {
			this.bringToFront(nodeId);
			this.nodeMap.get(nodeId).position = position;
		}
	}

	/** Bring a node to the front of the workspace */
	bringToFront(nodeId: ObjID) {
		const node = this.getNode(nodeId);
		if (node) {
			this.nodeMap.delete(nodeId);
			this.nodeMap.set(node.id, node);
		}
	}

	/** Check if a node is a trigger */
	isTrigger(nodeId: ObjID): boolean {
		return this.nodeMap.get(nodeId)?.type === NodeType.outputOnly;
	}

	/** Returns a flattened list of all bands in the LinkMap */
	getAllBands(): BandData[] {
		const bands: BandData[] = [];
		function flatten(value: BandState[], srcId: ObjID) {
			value.map((data: BandState) => bands.push({ srcId, destId: data.destId, type: data.type$?.value }));
		}
		this.linkMap.forEach(flatten);
		return bands;
	}

	/** Clears the LinkMap and closes all position emitters */
	clear(): void {
		// complete all position emitters
		this.linkMap.forEach(connections => {
			connections.forEach(this.unsubscribeBandListener);
		});
		// clear all map entries
		this.linkMap.clear();
		this.reverseLinks.clear();
		this.nodeMap.clear();
		this.workflowMap.clear();
	}

	/** Return the IDs of all nodes directly connected to a given node */
	getAllLinkedNodes(nodeId: ObjID): ObjID[] {
		return (this.linkMap.has(nodeId) ? this.linkMap.get(nodeId).map(band => band.destId) : []).concat(
			this.reverseLinks.has(nodeId) ? this.reverseLinks.get(nodeId) : []
		);
	}

	/** Returns a workflows by ID */
	getWorkflow(id: ObjID): Workflow {
		return this.workflowMap.get(id);
	}

	/** Returns the list of all workflows */
	getWorkflows(): Workflow[] {
		return Array.from(this.workflowMap.values());
	}

	/** Returns a list of bands related to a workflow */
	getAssociatedBands(wfId: ObjID): BandData[] {
		const nodes = this.workflowMap.get(wfId).nodes;
		const bands: BandData[] = [];
		function flatten(value: BandState[], srcId: ObjID) {
			value.map((data: BandState) => bands.push({ srcId, destId: data.destId, type: data.type$?.value }));
		}
		this.linkMap.forEach(flatten);
		return bands.filter(band => nodes.has(band.srcId));
	}

	/**
	 * Return true if a node overlaps with the provided position
	 * @param point - Position to check for overlap
	 * @param excludedNodes - list of node IDs to exclude from the search
	 * (excludedNodes is used so that a node can't overlap with itself or other selected nodes when moving a short distance)
	 */
	public nodeOverlapsAtPosition(point: Point, excludedNodes: ObjID[]): boolean {
		for (const node of this.nodeMap.values()) {
			if (
				!excludedNodes.includes(node.id) &&
				node.position.x < point.x + 7 * GRID_SIZE &&
				node.position.x > point.x - 7 * GRID_SIZE &&
				node.position.y === point.y
			) {
				return true;
			}
		}
		return false;
	}

	/** Returns the linkMap in a BE-supported format */
	getBandMapModel(wfId: ObjID): BandModel[] {
		return Array.from(this.linkMap, ([source, destination]) => ({ source, destination }))
			.map(bandState => ({
				source: bandState.source,
				destinations: bandState.destination.map(band => ({
					id: band.destId,
					type: band.type$.value === BandType.success ? 'OnSuccess' : 'OnFailure',
				})),
			}))
			.filter(band => this.workflowMap.get(wfId).nodes.has(band.source));
	}

	/** Initializes NodeMap, LinkMap, and WorkflowMap */
	initialize(workspace: Workspace) {
		this.clear();
		workspace.bands.forEach((data: BandData) => {
			const type$: BehaviorSubject<BandType> = new BehaviorSubject(data.type);
			this.addLink(data.srcId, { destId: data.destId, type$ });
		});
		workspace.nodes.forEach((node: Node) => {
			this.addNode(node);
		});
		workspace.workflows.forEach((workflow: Workflow) => {
			if (workflow) {
				this.addWorkflow(workflow);
			}
		});
	}

	/**
	 * Private Functions
	 */

	/** Completes a band's position emitter */
	private unsubscribeBandListener(state: BandState): void {
		return state.position$?.complete();
	}

	/** Removes all bands into and out of a given node */
	private removeAllLinksTo(nodeId: ObjID) {
		// Remove all output bands
		if (this.linkMap.has(nodeId)) {
			[...this.linkMap.get(nodeId)].forEach(band => {
				this.removeLink(nodeId, band.destId);
			});
		}
		// Remove all input bands
		if (this.reverseLinks.has(nodeId)) {
			[...this.reverseLinks.get(nodeId)].forEach(source => {
				this.removeLink(source, nodeId);
			});
		}
	}

	/** Adds a workflow to the workflowMap, and initializes its nodes and position */
	private addWorkflow(workflow: Workflow) {
		workflow.nodes.forEach(nodeId => {
			const node = this.nodeMap.get(nodeId);
			node.workflowId = workflow.id;
		});
		this.workflowMap.set(workflow.id, { ...workflow });
	}

	/** Checks if a path exists between two nodes */
	private isConnected(id1: ObjID, id2: ObjID): boolean {
		const queue = [id1];
		const visited: Set<ObjID> = new Set(queue);
		while (queue.length) {
			const curId = queue.shift();
			if (curId === id2) {
				return true;
			}
			this.getAllLinkedNodes(curId).forEach(id => {
				if (!visited.has(id)) {
					visited.add(id);
					queue.push(id);
				}
			});
		}
		return false;
	}

	/** Returns the IDs of all nodes in the same workflow, or null if the node does not belong to a workflow */
	private findWorkflowMembers(startId: ObjID): ObjID[] {
		const queue = [startId];
		const visited: Set<ObjID> = new Set(queue);
		let hasTrigger = false;
		while (queue.length) {
			const curId = queue.shift();
			if (this.nodeMap.get(curId).type === NodeType.outputOnly) {
				hasTrigger = true;
			}
			this.getAllLinkedNodes(curId).forEach(id => {
				if (!visited.has(id)) {
					visited.add(id);
					queue.push(id);
				}
			});
		}
		// A valid workflow has at least 1 trigger and 1 action
		if (hasTrigger && visited.size > 1) {
			return Array.from(visited);
		} else {
			// If the visited nodes do not form a workflow, remove them from their current workflow
			const workflow = this.workflowMap.get(this.nodeMap.get(startId).workflowId);
			Array.from(visited).forEach(nodeId => {
				workflow.nodes.delete(nodeId);
				this.nodeMap.get(nodeId).workflowId = undefined;
			});
			return null;
		}
	}

	/** Removes a band from the reverse LinkMap */
	private removeReverseLink(srcId: ObjID, destId: ObjID) {
		if (!this.reverseLinks.has(destId)) {
			return;
		}
		const links: ObjID[] = this.reverseLinks.get(destId);
		const idx: number = links.findIndex((source: ObjID) => source === srcId);
		if (idx < 0) {
			return;
		}
		links.splice(idx, 1);
		if (!links.length) {
			this.reverseLinks.delete(destId);
		}
	}

	/** Update workflow data structures when two nodes are connected */
	private updateWorkflowsOnConnect(srcId: ObjID, destId: ObjID) {
		const srcWorkflowId = this.nodeMap.get(srcId)?.workflowId;
		const destWorkflowId = this.nodeMap.get(destId)?.workflowId;

		if (srcWorkflowId && destWorkflowId) {
			// Case 1: Both nodes were already part of the same workflow (NO CHANGES)
			if (srcWorkflowId === destWorkflowId) {
				return;
			}
			// Case 2: Both nodes were part of different workflows (MERGE WORKFLOWS)
			else if (srcWorkflowId !== destWorkflowId) {
				const largerWorkflow =
					this.workflowMap.get(srcWorkflowId).nodes.size >= this.workflowMap.get(destWorkflowId).nodes.size
						? this.workflowMap.get(srcWorkflowId)
						: this.workflowMap.get(destWorkflowId);
				const smallerWorkflow =
					largerWorkflow.id === srcWorkflowId
						? this.workflowMap.get(destWorkflowId)
						: this.workflowMap.get(srcWorkflowId);
				// Larger workflow absorbs smaller workflow
				smallerWorkflow.nodes.forEach(nodeId => {
					largerWorkflow.nodes.add(nodeId);
					this.nodeMap.get(nodeId).workflowId = largerWorkflow.id;
				});
				this.workflowMap.delete(smallerWorkflow.id);
				return;
			}
		}
		// Case 3: One node was in a workflow while the other wasn't (JOIN WORKFLOW)
		else if ((srcWorkflowId && !destWorkflowId) || (!srcWorkflowId && destWorkflowId)) {
			const workflow = srcWorkflowId ? this.workflowMap.get(srcWorkflowId) : this.workflowMap.get(destWorkflowId);
			const nodeId = srcWorkflowId ? destId : srcId;
			this.updateWorkflowMembers(workflow, nodeId);
			return;
		}
		// Case 4: Neither node was in a workflow and one is a trigger (CREATE WORKFLOW)
		else if (!srcWorkflowId && !destWorkflowId && (this.isTrigger(srcId) || this.isTrigger(destId))) {
			const workflow: Workflow = {
				id: getUUID(),
				nodes: new Set<ObjID>(this.findWorkflowMembers(srcId)),
			};
			this.addWorkflow(workflow);
			return;
		}
		// Case 5: Neither node was in a workflow, and neither is a trigger (NO CHANGES)
	}

	/** Finds all nodes which are part of the same workflow and updates the workflow's nodes and nodes' workflowIds */
	private updateWorkflowMembers(workflow: Workflow, startNode: ObjID) {
		workflow.nodes.clear();
		this.findWorkflowMembers(startNode).forEach(nodeId => {
			workflow.nodes.add(nodeId);
			this.nodeMap.get(nodeId).workflowId = workflow.id;
		});
	}

	/** Update workflow data structures when two nodes are disconnected */
	private updateWorkflowsOnDisconnect(srcId: ObjID, destId: ObjID) {
		const workflowId = this.nodeMap.get(srcId)?.workflowId;
		const originalWorkflow = this.workflowMap.get(workflowId);
		// Case 1: Either both nodes are still part of the same workflow or there was no workflow to begin with (NO CHANGES)
		if (!workflowId || this.isConnected(srcId, destId)) {
			return;
		} else {
			// The two nodes are no longer part of the same workflow
			const srcWorkflowMembers = this.findWorkflowMembers(srcId);
			const destWorkflowMembers = this.findWorkflowMembers(destId);
			// Case 2: One node no longer belongs to a workflow but the other does (LEAVE WORKFLOW)
			if ((!srcWorkflowMembers && destWorkflowMembers) || (srcWorkflowMembers && !destWorkflowMembers)) {
				return;
			}
			// Case 3: Neither node belongs to a workflow anymore (DISBAND WORKFLOW)
			else if (!srcWorkflowMembers && !destWorkflowMembers) {
				this.workflowMap.delete(workflowId);
				return;
			}
			// Case 4: Both nodes are still part of a valid workflow (SPLIT WORKFLOW)
			else {
				const newWorkflow = { ...originalWorkflow, id: getUUID(), nodes: new Set<ObjID>() };
				// The larger half retains the original workflowId, with the source node acting as a tie-breaker
				if (srcWorkflowMembers.length >= originalWorkflow.nodes.size / 2) {
					destWorkflowMembers.forEach(nodeId => {
						originalWorkflow.nodes.delete(nodeId);
						newWorkflow.nodes.add(nodeId);
						this.nodeMap.get(nodeId).workflowId = newWorkflow.id;
					});
				} else {
					srcWorkflowMembers.forEach(nodeId => {
						originalWorkflow.nodes.delete(nodeId);
						newWorkflow.nodes.add(nodeId);
						this.nodeMap.get(nodeId).workflowId = newWorkflow.id;
					});
				}
				this.addWorkflow(newWorkflow);
				return;
			}
		}
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
